import {
  Path,
  FieldErrors,
  FieldValues,
  UseFormReturn,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormSetError,
} from 'react-hook-form';
import { captureException } from '@sentry/react';

import type { useFileUploader } from './useFileUploader.tsx';
import type { UploadFilesFormData, UploadFilesOnSubmitReturn } from './UploadFileModal.tsx';
import { MAX_INDIVIDUAL_DOCUMENT_SIZE_MB, MAX_TOTAL_DOCUMENTS_SIZE_MB } from './constants.ts';
import type { AcceptedFileType, AllAcceptedFileTypes, FileUploadData, FileUploadType } from './types.ts';

const MAX_INDIVIDUAL_FILE_SIZE = MAX_INDIVIDUAL_DOCUMENT_SIZE_MB * 1024 * 1024;

type Result<T, E> = { ok: true; value: T } | { ok: false; errors: E[] };

const Ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
const Err = <E>(errors: E[]): Result<never, E> => ({ ok: false, errors });

type FileValidationError =
  | { type: 'INVALID_FILE_TYPE'; acceptedTypes: string[] }
  | { type: 'MAX_INDIVIDUAL_FILE_SIZE'; limitMB: number; actualMB: number }
  | { type: 'MAX_TOTAL_FILE_SIZE'; limitMB: number; actualMB: number }
  | { type: 'MAX_FILES_UPLOAD'; maxFiles: number; currentCount: number };

type ValidationContext = {
  allowZip: boolean | undefined;
  remainingSlots: number;
  maxFilesUpload: number;
  currentTotalSize: number;
  maxTotalFileSize: number;
  acceptedFileTypes: AllAcceptedFileTypes;
  processedFilesCount: number;
};

type ValidatorFn = (file: File, ctx: ValidationContext) => FileValidationError | null;

const validateFileType: ValidatorFn = (file, ctx) => {
  const isValidType = isValidFileType({
    fileType: file.type,
    allowZip: ctx.allowZip,
    acceptedFileTypes: ctx.acceptedFileTypes,
  });

  if (!isValidType) {
    const acceptedTypes = getFormattedFileTypes({
      allowZip: ctx.allowZip,
      types: ctx.acceptedFileTypes,
    });
    return {
      type: 'INVALID_FILE_TYPE',
      acceptedTypes: [acceptedTypes],
    };
  }

  return null;
};

const validateIndividualFileSize: ValidatorFn = (file) => {
  if (file.size > MAX_INDIVIDUAL_FILE_SIZE) {
    return {
      type: 'MAX_INDIVIDUAL_FILE_SIZE',
      limitMB: MAX_INDIVIDUAL_DOCUMENT_SIZE_MB,
      actualMB: +(file.size / 1024 / 1024).toFixed(2),
    };
  }
  return null;
};

const validateTotalFileSize: ValidatorFn = (file, ctx) => {
  const projectedSize = ctx.currentTotalSize + file.size;
  const maxBytes = ctx.maxTotalFileSize * 1024 * 1024;

  if (projectedSize > maxBytes) {
    return {
      type: 'MAX_TOTAL_FILE_SIZE',
      limitMB: ctx.maxTotalFileSize,
      actualMB: +(projectedSize / 1024 / 1024).toFixed(2),
    };
  }
  return null;
};

const validateFileCount: ValidatorFn = (_, ctx) => {
  if (ctx.processedFilesCount >= ctx.remainingSlots) {
    return {
      type: 'MAX_FILES_UPLOAD',
      maxFiles: ctx.maxFilesUpload,
      currentCount: ctx.processedFilesCount,
    };
  }
  return null;
};

const defaultValidators: ValidatorFn[] = [
  validateFileType,
  validateIndividualFileSize,
  validateTotalFileSize,
  validateFileCount,
];

const validateFile = ({
  file,
  context,
  validators = defaultValidators,
}: {
  file: File;
  context: ValidationContext;
  validators?: ValidatorFn[];
}): Result<File, FileValidationError> => {
  const errors = validators.map((validator) => validator(file, context)).filter((error) => error !== null);

  return errors.length > 0 ? Err(errors) : Ok(file);
};

const createErrorMessage = (error: FileValidationError): string => {
  switch (error.type) {
    case 'INVALID_FILE_TYPE':
      return `The file type you uploaded isn't supported, please only upload ${error.acceptedTypes.join(', ')} file types.`;

    case 'MAX_INDIVIDUAL_FILE_SIZE':
      return `The file size should not exceed ${error.limitMB}MB, current file is ${error.actualMB}MB.`;

    case 'MAX_TOTAL_FILE_SIZE':
      return `The total file size should not exceed ${error.limitMB}MB, current total would be ${error.actualMB}MB.`;

    case 'MAX_FILES_UPLOAD':
      return `Only ${error.maxFiles} file${error.maxFiles > 1 ? 's' : ''} can be uploaded at once. Maximum limit reached.`;

    default:
      return 'An unknown error occurred during file validation.';
  }
};

type FileProcessingResult = {
  errors: FileValidationError[];
  validFiles: Array<{ file: File }>;
  totalSizeAdded: number;
  filesProcessed: number;
};

const processFilesWithValidation = ({
  files,
  context,
  validators = defaultValidators,
}: {
  files: File[];
  context: ValidationContext;
  validators?: ValidatorFn[];
}): FileProcessingResult =>
  files.reduce<FileProcessingResult>(
    (acc, file) => {
      const validationResult = validateFile({ file, context, validators });

      if (validationResult.ok) {
        return {
          ...acc,
          validFiles: [...acc.validFiles, { file: validationResult.value }],
          totalSizeAdded: acc.totalSizeAdded + file.size,
          filesProcessed: acc.filesProcessed + 1,
        };
      }

      return {
        ...acc,
        errors: [...acc.errors, ...validationResult.errors],
      };
    },
    {
      validFiles: [],
      errors: [],
      totalSizeAdded: 0,
      filesProcessed: 0,
    },
  );

type BaseArray = { file: File }[];

// FUTURE: Fix types to generalise so it could work with other form types not just UploadFilesFormData
// methods: UseFormReturn<InferFieldArray<NoInfer<TAppend>>['values']>;
// name: InferFieldArray<NoInfer<TAppend>>['name'];
// errors: FieldErrors<InferFieldArray<NoInfer<TAppend>>['values']>;
type HandleFilesUploadParams<TAppend extends UseFieldArrayAppend<UploadFilesFormData, 'files'>> = {
  append: TAppend;
  files: FileList;
  methods: UseFormReturn<UploadFilesFormData>;
  errors: FieldErrors<UploadFilesFormData>;
  maxFilesUpload: number;
  numberOfUploadFields: number;
  acceptedFileTypes: AllAcceptedFileTypes;
  allowZip: boolean | undefined;
};

export const handleFilesUpload = <TAppend extends UseFieldArrayAppend<UploadFilesFormData, 'files'>>({
  append,
  files,
  methods,
  errors,
  numberOfUploadFields = 1,
  maxFilesUpload,
  acceptedFileTypes,
  allowZip,
}: HandleFilesUploadParams<TAppend>) => {
  const uploadedFiles = methods.getValues('files') as BaseArray;
  const currentTotalSize = uploadedFiles?.reduce((acc, { file }) => acc + file.size, 0) ?? 0;
  const remainingSlots = maxFilesUpload - uploadedFiles.length;

  if (remainingSlots <= 0) {
    methods.setError('files', {
      message: `Only ${maxFilesUpload} file${maxFilesUpload > 1 ? 's' : ''} can be uploaded at once. Maximum limit reached.`,
    });

    return;
  }

  // Clear existing errors
  if (errors['files']) {
    methods.clearErrors('files');
  }

  const validFiles = Array.from(files).filter((file) => file.size > 0 && file.type !== '');
  const maxTotalFileSize = MAX_TOTAL_DOCUMENTS_SIZE_MB / numberOfUploadFields;

  const context: ValidationContext = {
    acceptedFileTypes,
    allowZip,
    remainingSlots,
    currentTotalSize,
    maxTotalFileSize,
    maxFilesUpload,
    processedFilesCount: 0,
  };

  const { validFiles: filesToAdd, errors: validationErrors } = processFilesWithValidation({
    files: validFiles,
    context,
  });

  // Add valid files to form
  filesToAdd.forEach(({ file }) => append({ file }));

  // Handle validation errors
  if (validationErrors.length > 0) {
    const errorMessages = validationErrors.map(createErrorMessage);
    const uniqueMessages = [...new Set(errorMessages)];

    methods.setError('files', {
      message: uniqueMessages.join(' '),
    });
  }
};

export const handleRemoveFile = <TForm extends FieldValues, TFieldName extends Path<NoInfer<TForm>>>({
  index,
  remove,
  errors,
  methods,
  name,
  maxFilesUpload,
}: {
  index: number;
  remove: UseFieldArrayRemove;
  methods: UseFormReturn<TForm>;
  errors: FieldErrors<NoInfer<TForm>>;
  name: TFieldName;
  maxFilesUpload: number;
}) => {
  remove(index);

  if (errors[name] && methods.getValues(name).length < maxFilesUpload) {
    methods.clearErrors(name);
  }
};

const SIZES = [' bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (!bytes) {
    return '0 bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${SIZES[i]}`;
};

type GenerateFileUploadData<TFileUploadType extends FileUploadType> = (params: {
  file: File;
}) => FileUploadData<TFileUploadType>;

export const generateUploadPromises = async <TFileUploadType extends FileUploadType>({
  files,
  generateFileUploadData,
  uploader,
}: {
  files: UploadFilesFormData['files'];
  generateFileUploadData: GenerateFileUploadData<TFileUploadType>;
  uploader: ReturnType<typeof useFileUploader>['uploader'];
}) => {
  const uploadPromises = files.map(async ({ file }, index) => {
    try {
      const fileData = generateFileUploadData({ file });

      const { upload } = await uploader(fileData);

      const result = await upload();

      if (result.success) {
        return {
          id: result.id,
          name: file.name,
          status: 'fulfilled' as const,
          index: index,
          uploadTask: result.uploadTask,
        };
      }

      return {
        id: result.id,
        name: file.name,
        status: 'rejected' as const,
        index: index,
        reason: new Error('Upload task not found'),
      };
    } catch (error) {
      captureException(error, {
        tags: {
          issueIn: 'generateUploadPromises',
        },
        extra: { file },
      });

      return {
        id: null,
        name: file.name,
        status: 'rejected' as const,
        index: index,
        reason: error,
      };
    }
  }) satisfies Promise<UploadFilesOnSubmitReturn>[];

  return Promise.all(uploadPromises).then((results) => {
    const failedUploads = results.filter((result) => result.status === 'rejected');
    const successfulUploads = results.filter((result) => result.status === 'fulfilled');

    if (failedUploads.length > 0) {
      throw new Error('Failed to upload', {
        cause: {
          failedUploads,
          successfulUploads,
        },
      });
    }

    return results;
  });
};

export const mimeTypeToFriendlyName: Record<AcceptedFileType, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
};

/**
 * Generates the custom metadata object for a given file upload.
 * NOTE: This is used to flatten the metadata object to a single level, and only include strings.
 * @param metadata - The metadata object to generate a custom metadata object for.
 * @returns A custom metadata object.
 */
export const generateCustomMetadata = <TFileUploadType extends FileUploadType>(
  metadata: Pick<FileUploadData<TFileUploadType>, 'fileName' | 'ownerId' | 'uploaderId' | 'metadata'> & {
    fileUploadDocId: string;
    fileUploadType: TFileUploadType;
  },
) => {
  const flattenedMetadata: Record<string, string> = {};

  for (const [key, value] of Object.entries(metadata)) {
    if (key === 'metadata' && typeof value === 'object' && value !== null) {
      for (const [metaKey, metaValue] of Object.entries(value)) {
        if (typeof metaValue === 'string') {
          flattenedMetadata[metaKey] = metaValue;
        }
      }
    } else if (typeof value === 'string') {
      flattenedMetadata[key] = value;
    }
  }

  return { customMetadata: flattenedMetadata };
};

/**
 * Generates a Google Cloud Storage URL for a given file path.
 */
export const getFileUploadUrl = (storagePath: string) =>
  storagePath ? `gs://${import.meta.env.VITE_APP_FIREBASE_CONFIG_STORAGE_BUCKET_UNSCANNED}/${storagePath}` : '';

export const handleFileUploadError = <TFileUploadType extends FileUploadType>({
  error,
  issueDescription,
  fileData,
}: {
  error: unknown;
  issueDescription: string;
  fileData: FileUploadData<TFileUploadType>;
}) => {
  const sentryIssueId = captureException(error, {
    tags: {
      issueIn: `handleFileUploadError`,
    },
    extra: { fileData, description: issueDescription },
  });

  return { error, sentryIssueId } as const;
};

const formatAcceptedTypes = (types: AllAcceptedFileTypes): string[] => {
  if (types === 'ALL_TYPES') {
    return [];
  }

  return types?.length ? mimeTypesToFriendlyNames(types) : [];
};

const addZipIfAllowed = ({ types, allowZip }: { types: string[]; allowZip: boolean | undefined }): string[] =>
  allowZip ? [...types, 'ZIP'] : types;

const formatTypesList = (types: string[]): string =>
  types.length > 1 ? `${types.slice(0, -1).join(', ')} or ${types.slice(-1)}` : types.join('');

const mimeTypesToFriendlyNames = (mimeTypes: AllAcceptedFileTypes) => {
  if (mimeTypes === 'ALL_TYPES') {
    return [];
  }

  return mimeTypes?.map((mimeType) => mimeTypeToFriendlyName[mimeType]) ?? [];
};

export const getFormattedFileTypes = ({
  types,
  allowZip,
}: {
  types: AllAcceptedFileTypes;
  allowZip: boolean | undefined;
}): string => {
  const allTypes = addZipIfAllowed({ types: formatAcceptedTypes(types), allowZip });

  return formatTypesList(allTypes);
};

type FileSystemEntry = {
  isFile: boolean;
  isDirectory: boolean;
  file: (callback: (file: File) => void) => void;
  createReader: () => {
    readEntries: (callback: (entries: FileSystemEntry[]) => void) => void;
  };
  name: string;
};

/**
 * Recursively traverse the directory and get all files
 * @param item
 * @param path
 * @returns
 */
export const traverseFileTree = async (item: FileSystemEntry, path: string = ''): Promise<File[]> => {
  if (item.isFile) {
    return new Promise((resolve) => {
      item.file((file: File) => {
        resolve([file]);
      });
    });
  }

  if (!item.isDirectory) {
    return [];
  }

  const dirReader = item.createReader();
  const entries: FileSystemEntry[] = await new Promise((resolve) => {
    dirReader.readEntries(resolve);
  });
  const filesInSubDirectories = await Promise.all(
    entries.map((entry) => traverseFileTree(entry, path + item.name + '/')),
  );

  return filesInSubDirectories.flat();
};

export const handleFileDrop = async ({
  event,
  acceptedFileTypes,
  allowZip,
  setError,
  id,
  onChange,
}: {
  event: React.DragEvent<HTMLDivElement>;
  acceptedFileTypes: AllAcceptedFileTypes;
  allowZip: boolean | undefined;
  setError: UseFormSetError<any>;
  id: string;
  onChange: (files: FileList) => void;
}) => {
  event.preventDefault();

  const items = event.dataTransfer.items;

  if (!items) {
    const files = event.dataTransfer.files;
    if (files.length === 0) {
      return;
    }
    onChange(files);

    return;
  }

  const allFiles: File[] = [];
  for (const item of Array.from(items)) {
    const isValid = isValidFileType({ fileType: item.type, acceptedFileTypes, allowZip });

    if (!isValid) {
      setError(id, {
        types: {
          invalidFileType: getInvalidFileTypeMessage({ acceptedFileTypes, allowZip }),
        },
      });

      continue;
    }

    if (item.kind === 'file' && item.type !== '') {
      allFiles.push(item.getAsFile() as File);

      continue;
    }

    const entry = item.webkitGetAsEntry() as FileSystemEntry | null;

    if (entry) {
      const files = await traverseFileTree(entry);
      allFiles.push(...files);
    }
  }

  if (allFiles.length === 0) {
    return;
  }

  onChange(allFiles as unknown as FileList);
};

export const isValidFileType = ({
  fileType,
  acceptedFileTypes,
  allowZip,
}: {
  fileType: string;
  acceptedFileTypes: AllAcceptedFileTypes;
  allowZip: boolean | undefined;
}) => {
  if (fileType === 'application/zip') {
    return !!allowZip;
  }

  if (acceptedFileTypes === 'ALL_TYPES') {
    return true;
  }

  return acceptedFileTypes.includes(fileType as AcceptedFileType);
};

export const getInvalidFileTypeMessage = ({
  acceptedFileTypes,
  allowZip,
}: {
  acceptedFileTypes: AllAcceptedFileTypes;
  allowZip: boolean | undefined;
}) => {
  const formattedTypes = getFormattedFileTypes({ types: acceptedFileTypes, allowZip });

  return `Unsupported file type. Please only upload ${formattedTypes} file types.`;
};
